import { ref } from 'vue';
import { defineStore } from 'pinia';
import { UnCachedProduct, updateProdWithUnCachedData, useProductsStore, type Product } from '~~/common/stores/products';
import { useNuxtApp } from 'nuxt/app';
import { AW_SF_RECOMMENDATION, type SfmcRecommendation } from '~~/common/config/RecommendationConfig';

interface SfRecommendationWithProducts extends SfmcRecommendation {
  products?: Product[];
}

function isObjectRecord (value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export const useAwSfRecommendationStore = defineStore('awSfRecommendation', () => {
  const nuxtApp = useNuxtApp();

  const recommendations = ref<SfRecommendationWithProducts[]>([]);
  const cacheKey = ref<string | null>(null);

  function setCacheKey (key: string | null) {
    cacheKey.value = key;
  }

  async function fetchSFRecommendation (recos: SfmcRecommendation[]) {
    clearRecommendation();

    // only first reco display needed
    const reco = recos[0];
    if (!reco) {
      return;
    }

    const productsStore = useProductsStore();
    setLocalStorageCacheItem(cacheKey.value, reco);

    const ids = reco.items?.map(product => {
      return product.product_code;
    }) ?? [];

    if (ids.length > 0) {
      const params = {
        isCached: true,
        page: 1,
        itemsPerPage: 16,
        auchanCodes: ids,
        filterParams: {
          available: 1,
        },
      };

      const {
        value: data,
        isCached,
      } = await productsStore.fetchCachedProductOrFallback({ params });

      recommendations.value = [{
        ...reco,
        products: data?.results,
      }];

      if (isCached) {
        const resp = await productsStore.getUnCachedPartsReq({ productIds: (data?.results?.map(product => product.id) || []) });
        if (resp?.list && Object.keys(resp.list).length) {
          for (const k in resp.list) {
            const entry = resp.list[k];
            if (entry) {
              resp.list[k] = new UnCachedProduct(entry);
            }
          }
          const target = recommendations.value.find(rec => rec.name === reco.name)?.products;
          if (target) {
            updateProdWithUnCachedData({
              productsToMutate: target,
              payload: resp.list,
              $logger: nuxtApp.$logger,
            });
          }
        }
      }
    } else {
      recommendations.value = [{
        ...reco,
        products: [],
      }];
    }


    recommendations.value = recommendations.value.sort((a, b) => (a.priority > b.priority) ? 1 : -1);
  }

  function clearRecommendation () {
    recommendations.value = [];
  }

  function setLocalStorageCacheItem (key: string | null, value: SfmcRecommendation) {
    if (!key) {
      return;
    }
    const { $config } = nuxtApp;
    const parsed: unknown = JSON.parse(localStorage.getItem(AW_SF_RECOMMENDATION) ?? 'null');
    const awSfmcRecommendation: Record<string, unknown> = isObjectRecord(parsed) ? parsed : {};
    if (!(key in awSfmcRecommendation)) {
      awSfmcRecommendation[key] = { validTo: (new Date().getTime()) + $config.public.sfmcCacheExpirationMs, recommendation: value };
      localStorage.setItem(AW_SF_RECOMMENDATION, JSON.stringify(awSfmcRecommendation));
    }
  }

  return {
    recommendations,
    cacheKey,
    setCacheKey,
    fetchSFRecommendation,
    clearRecommendation,
  };
});
